import * as PIXI from 'pixi.js';
import { WireBundle, Connector, HarnessNode } from '../types';
import { BRAND, DIMENSIONS } from '../utils/constants';
import { GeometryUtils, connectorCenter } from '../utils/geometry';
import { drawDottedLine, DottedLineStyle } from '../utils/drawing';
import { logger } from '../utils/logger';

const PREVIEW_LINE_STYLE: DottedLineStyle = {
  dotSpacing: 10,
  dotLength: 5,
  width: DIMENSIONS.WIRE_THICKNESS,
  color: BRAND.GOLD, // Gold preview colour
  alpha: 0.7
};

export class BundlePreview extends PIXI.Container {
  private graphics: PIXI.Graphics;
  private bundleData: WireBundle;
  private previewNodePosition: { x: number; y: number };
  private connectors: Connector[];
  private nodes: HarnessNode[];

  constructor(bundleData: WireBundle, previewNodePosition: { x: number; y: number }, connectors: Connector[], nodes: HarnessNode[]) {
    super();
    
    logger.bundle.debug('BundlePreview constructor called', {
      bundleId: bundleData.id,
      wireCount: bundleData.wires.length,
      previewPos: previewNodePosition,
      connectorCount: connectors.length,
      nodeCount: nodes.length
    });
    
    this.bundleData = bundleData;
    this.previewNodePosition = previewNodePosition;
    this.connectors = connectors;
    this.nodes = nodes;
    
    this.graphics = new PIXI.Graphics();
    this.addChild(this.graphics);
    
    this.render();
  }

  updatePreviewPosition(x: number, y: number): void {
    this.previewNodePosition = { x, y };
    this.render();
  }

  private render(): void {
    logger.bundle.debug(`BundlePreview render() called, drawing ${this.bundleData.wires.length} wire previews`);
    this.graphics.clear();

    // For each wire in the bundle, show the rerouted path
    this.bundleData.wires.forEach((wire, index) => {
      logger.bundle.debug(`Drawing preview for wire ${index + 1}: ${wire.name}`);
      this.drawWirePreview(wire);
    });
    
    logger.bundle.debug('BundlePreview render complete');
  }

  private drawWirePreview(wire: any): void {
    logger.bundle.debug(`drawWirePreview called for wire: ${wire.name} from ${wire.startConnectorId} to ${wire.endConnectorId}`);
    
    const startConnector = this.connectors.find(t => t.id === wire.startConnectorId);
    const endConnector = this.connectors.find(t => t.id === wire.endConnectorId);
    
    logger.bundle.debug(`Found connectors: start=${!!startConnector}, end=${!!endConnector}`);
    
    if (!startConnector || !endConnector) {
      logger.bundle.debug('Missing connectors, skipping wire preview');
      return;
    }

    // Calculate the optimal insertion index for the preview node
    const insertIndex = this.calculateOptimalInsertIndex(wire);
    logger.bundle.debug(`Insert index calculated: ${insertIndex}`);
    
    // Build the complete path with the preview node inserted
    const pathPoints = this.buildPreviewPath(wire, insertIndex, startConnector, endConnector);
    logger.bundle.debug(`Path points built: ${pathPoints.length} points`);
    
    // Draw the dotted line through all path points
    for (let i = 0; i < pathPoints.length - 1; i++) {
      drawDottedLine(this.graphics, pathPoints[i], pathPoints[i + 1], PREVIEW_LINE_STYLE);
    }
  }

  private buildPreviewPath(wire: any, insertIndex: number, startConnector: Connector, endConnector: Connector): Array<{ x: number; y: number }> {
    const pathPoints = [];

    // Start point (connector center)
    pathPoints.push(connectorCenter(startConnector));

    // Add existing nodes up to insertion point
    for (let i = 0; i < insertIndex; i++) {
      if (i < wire.path.length) {
        const node = this.nodes.find(n => n.id === wire.path[i]);
        if (node) {
          pathPoints.push({ x: node.xPosition, y: node.yPosition });
        }
      }
    }
    
    // Add preview node
    pathPoints.push({ x: this.previewNodePosition.x, y: this.previewNodePosition.y });
    
    // Add remaining existing nodes
    for (let i = insertIndex; i < wire.path.length; i++) {
      const node = this.nodes.find(n => n.id === wire.path[i]);
      if (node) {
        pathPoints.push({ x: node.xPosition, y: node.yPosition });
      }
    }
    
    // End point (connector center)
    pathPoints.push(connectorCenter(endConnector));

    return pathPoints;
  }

  private calculateOptimalInsertIndex(wire: any): number {
    const startConnector = this.connectors.find(t => t.id === wire.startConnectorId);
    const endConnector = this.connectors.find(t => t.id === wire.endConnectorId);
    
    if (!startConnector || !endConnector) {
      return wire.path.length;
    }

    const pathPoints = [
      connectorCenter(startConnector),
      ...wire.path.map((nodeId: string) => {
        const node = this.nodes.find(n => n.id === nodeId);
        return node ? { x: node.xPosition, y: node.yPosition } : null;
      }).filter(Boolean),
      connectorCenter(endConnector)
    ];
    
    // Find the best position to insert the new node
    let bestIndex = 0;
    let minDistance = Infinity;
    
    for (let i = 0; i < pathPoints.length - 1; i++) {
      const start = pathPoints[i];
      const end = pathPoints[i + 1];
      
      // Calculate distance from the preview node to the line segment
      const distance = GeometryUtils.distanceToLine(this.previewNodePosition, start, end);

      if (distance < minDistance) {
        minDistance = distance;
        bestIndex = i; // Insert after this point
      }
    }
    
    return bestIndex;
  }

}